import React, { useEffect, useState } from "react";
import "./Teachings.css";
import axiosInstance from "../../axiosinstance";
// navigate is not used here; keep for future navigation
import { useAutoHorizontalScroll } from "../../hooks/useAutoHorizontalScroll";
import Button from "../button/Button";
import { motion, Variants } from "framer-motion";
import { fadeIn, staggerContainer } from "../../utils/motion";
import { navigateTo } from "../../utils/navigateTo";

interface Teaching {
  _id: string;
  title: string;
  videoLink?: string;
  publishDate?: string;
  createdAt?: string;
}

const containerVariants: Variants = {
  visible: {
    transition: { staggerChildren: 0.18 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeInOut" },
  },
};

// Helper to safely extract a YouTube embed URL
function getYouTubeEmbedUrl(url: string): string {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  const videoId = match ? match[1] : url;
  return `https://www.youtube.com/embed/${videoId}`;
}

function Teachings() {
  const [teachings, setTeachings] = useState<Teaching[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const teachingsRes = await axiosInstance.get("/teachings", {
          params: { limit: 2, sort: "newest" },
        });

        const rawTeachings = teachingsRes.data;
        const teachingsArr: Teaching[] = Array.isArray(rawTeachings)
          ? rawTeachings
          : Object.values(rawTeachings);
        setTeachings(
          teachingsArr
            .slice()
            .sort((a, b) => {
              const dateA = Date.parse(a.createdAt || a.publishDate || "") || 0;
              const dateB = Date.parse(b.createdAt || b.publishDate || "") || 0;
              return dateB - dateA;
            })
            .slice(0, 2)
        );
      } catch {
        setTeachings([]);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const videoScrollRef = useAutoHorizontalScroll({
    speed: 0.5,
    pauseOnHover: true,
  });

  return (
    <motion.section
      className="teachings-container"
      variants={staggerContainer(0.2, 0.3)}
      initial="hidden"
      whileInView="show"
      viewport={{ once: false, amount: 0.25 }}
    >
      <motion.h2
        className="blogs-header comp tt"
        variants={fadeIn("up", "spring", 0.1, 0.8)}
      >
        Latest Video Teachings
      </motion.h2>

      <motion.div
        className="teachings-horizontal-scroll"
        ref={videoScrollRef}
        variants={fadeIn("up", "spring", 0.1, 1.3)}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>Loading...</div>
        ) : teachings.length ? (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{ display: "flex", gap: "2rem" }}
          >
            {teachings.map((t) => (
              <motion.div
                className="single-teaching-card"
                key={t._id}
                variants={itemVariants}
              >
                <div>
                  {t.videoLink ? (
                    <iframe
                      src={getYouTubeEmbedUrl(t.videoLink)}
                      title={t.title}
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <div className="single-teaching-no-video">No Video</div>
                  )}
                </div>

                <div className="single-teaching-card-title">{t.title}</div>

                <div className="single-teaching-card-date">
                  {t.publishDate &&
                    `Published: ${new Date(t.publishDate).toLocaleDateString(
                      "en-US",
                      { year: "numeric", month: "short", day: "numeric" }
                    )}`}
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            No teachings found.
          </div>
        )}
      </motion.div>

      <div className="teachings-viewall-btn">
        <Button
          text="View More Inspiring Video Teachings"
          onClick={() => navigateTo("/teachings")}
        />
      </div>
    </motion.section>
  );
}

export default Teachings;
